import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Save, Clock3, ChevronDown, ChevronRight, HardHat, ShieldCheck } from "lucide-react";

import { getJson } from "@/lib/http";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BriefingRichTextEditor } from "@/components/editor/BriefingRichTextEditor";

type RetailerStandardsLifecycleStatus = "LIVE" | "SCHEDULED" | "DRAFT" | "ARCHIVED";

type RetailerStandardsVersionDto = {
  id: number;
  retailerId: number;
  version: number;
  status: "DRAFT" | "ACTIVE" | "ARCHIVED";
  effectiveFrom: string;
  changeSummary: string | null;
  installationStandards: string;
  removalStandards: string;
  conductInStore: string;
  submissionRequirements: string;
  imageQualityForReview: string;
  createdBy: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  archivedAt: string | null;
  archivedBy: number | null;
  createdByName: string | null;
  archivedByName: string | null;
  lifecycleStatus: RetailerStandardsLifecycleStatus;
};

type RetailerStandardsResponse = {
  retailerId: number;
  activeVersion: RetailerStandardsVersionDto | null;
  versions: RetailerStandardsVersionDto[];
};

type FieldKey =
  | "installationStandards"
  | "removalStandards"
  | "conductInStore"
  | "submissionRequirements"
  | "imageQualityForReview";

type FormState = Record<FieldKey, string> & { changeSummary: string };

const INSTALLER_FIELDS: { key: FieldKey; label: string; placeholder: string }[] = [
  { key: "installationStandards", label: "Installation Standards", placeholder: "Define placement and execution standards." },
  { key: "removalStandards", label: "Removal Standards", placeholder: "Define removal procedures and expectations." },
  { key: "conductInStore", label: "Conduct in Store", placeholder: "Expected behaviour, dress code, store etiquette." },
  { key: "submissionRequirements", label: "Submission Requirements", placeholder: "Required images, evidence, and submission rules." },
];

const ADMIN_FIELDS: { key: FieldKey; label: string; placeholder: string }[] = [
  { key: "imageQualityForReview", label: "Image Quality for Retailer Review", placeholder: "Define what retailer-ready image quality looks like." },
];

const ALL_FIELD_KEYS: FieldKey[] = [...INSTALLER_FIELDS, ...ADMIN_FIELDS].map((f) => f.key);

function formFromVersion(version?: RetailerStandardsVersionDto | null): FormState {
  return {
    changeSummary: "",
    installationStandards: version?.installationStandards || "",
    removalStandards: version?.removalStandards || "",
    conductInStore: version?.conductInStore || "",
    submissionRequirements: version?.submissionRequirements || "",
    imageQualityForReview: version?.imageQualityForReview || "",
  };
}

function hasChanges(form: FormState, version?: RetailerStandardsVersionDto | null): boolean {
  return ALL_FIELD_KEYS.some((key) => (form[key] || "") !== (version?.[key] || ""));
}

function StatusBadge({ lifecycleStatus }: { lifecycleStatus: RetailerStandardsLifecycleStatus }) {
  switch (lifecycleStatus) {
    case "LIVE":
      return <Badge className="bg-green-100 text-green-800 border-green-200">Live</Badge>;
    case "SCHEDULED":
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Scheduled</Badge>;
    case "ARCHIVED":
      return <Badge variant="secondary">Archived</Badge>;
    case "DRAFT":
      return <Badge variant="outline">Draft</Badge>;
  }
}

function SectionHeading({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h4>
    </div>
  );
}

function VersionHistoryRow({ version }: { version: RetailerStandardsVersionDto }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-lg">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <StatusBadge lifecycleStatus={version.lifecycleStatus} />
          <span className="font-medium text-sm">v{version.version}</span>
          {version.changeSummary && (
            <span className="text-sm text-muted-foreground truncate">{version.changeSummary}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground">
            {version.createdAt ? format(new Date(version.createdAt), "dd MMM yyyy, HH:mm") : "—"}
          </span>
          {version.createdByName && (
            <span className="text-xs text-muted-foreground">{version.createdByName}</span>
          )}
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t">
          <div className="pt-3 grid gap-3 md:grid-cols-2">
            {[...INSTALLER_FIELDS, ...ADMIN_FIELDS].map(({ key, label }) => (
              <div key={key} className="rounded-md bg-muted/50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
                <p className="text-sm whitespace-pre-wrap">{version[key] || "—"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function RetailerStandardsTab({ retailerId, retailerName }: { retailerId: number; retailerName: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["retailers", retailerId, "standards"], [retailerId]);

  const { data, isLoading } = useQuery<RetailerStandardsResponse>({
    queryKey,
    queryFn: () => getJson(`/api/retailers/${retailerId}/standards`),
    enabled: !!retailerId,
  });

  const activeVersion = data?.activeVersion || null;
  const [formState, setFormState] = useState<FormState | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const currentForm = formState ?? formFromVersion(activeVersion);
  const dirty = hasChanges(currentForm, activeVersion);

  const updateField = useCallback((key: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...(prev ?? formFromVersion(activeVersion)), [key]: value }));
  }, [activeVersion]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return apiRequest(`/api/retailers/${retailerId}/standards/versions`, "POST", {
        ...currentForm,
        status: "ACTIVE",
        effectiveFrom: new Date().toISOString(),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      setFormState(null);
      toast({ title: "Standards updated", description: "A new version has been saved and activated." });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to save standards",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const discard = useCallback(() => {
    setFormState(null);
  }, []);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-6">Loading retailer standards...</div>;
  }

  const pastVersions = (data?.versions || []).filter((v) => v.id !== activeVersion?.id);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Retailer Standards</h3>
        <p className="text-sm text-muted-foreground">
          Standards for {retailerName}. Edit any field below and save — each save creates a new versioned record.
        </p>
      </div>

      {activeVersion && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock3 className="h-3.5 w-3.5" />
          <span>
            Version {activeVersion.version}, effective {format(new Date(activeVersion.effectiveFrom), "dd MMM yyyy, HH:mm")}
            {activeVersion.createdByName && <> by {activeVersion.createdByName}</>}
          </span>
        </div>
      )}

      <Card>
        <CardContent className="pt-6 space-y-5">
          <SectionHeading icon={HardHat}>For Installers</SectionHeading>
          {INSTALLER_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key} className="space-y-1.5">
              <Label>{label}</Label>
              <BriefingRichTextEditor
                content={currentForm[key]}
                onChange={(html) => updateField(key, html)}
                placeholder={placeholder}
              />
            </div>
          ))}

          <SectionHeading icon={ShieldCheck}>For Administrators</SectionHeading>
          {ADMIN_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key} className="space-y-1.5">
              <Label>{label}</Label>
              <BriefingRichTextEditor
                content={currentForm[key]}
                onChange={(html) => updateField(key, html)}
                placeholder={placeholder}
              />
            </div>
          ))}

          {dirty && (
            <div className="pt-2 space-y-3 border-t">
              <div className="space-y-1.5">
                <Label htmlFor="standards-change-summary">What changed? <span className="font-normal text-muted-foreground">(optional)</span></Label>
                <Input
                  id="standards-change-summary"
                  value={currentForm.changeSummary}
                  onChange={(e) => updateField("changeSummary", e.target.value)}
                  placeholder="Brief note on what you changed"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  {saveMutation.isPending ? "Saving..." : "Save"}
                </Button>
                <Button variant="outline" onClick={discard} disabled={saveMutation.isPending}>
                  Discard Changes
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {pastVersions.length > 0 && (
        <Card>
          <CardHeader
            className="cursor-pointer select-none"
            onClick={() => setHistoryOpen(!historyOpen)}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Version History</CardTitle>
                <CardDescription>{pastVersions.length} previous version{pastVersions.length !== 1 ? "s" : ""}</CardDescription>
              </div>
              {historyOpen ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
            </div>
          </CardHeader>
          {historyOpen && (
            <CardContent className="space-y-2">
              {pastVersions.map((version) => (
                <VersionHistoryRow key={version.id} version={version} />
              ))}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
